import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useRef } from "react";
import { useStringMap } from "../utilities/string-map/useStringMap";
import { useCombineRefs } from "../hooks/useCombineRefs";
import * as Styled from "./OverflowScroller.styled";
import { useOverflowState } from "./useOverflowState";
import { OverflowIndicator } from "./OverflowIndicator";
import { useOverflowCallbacks } from "./useOverflowCallbacks";
import { defaults } from "./OverflowScroller.constants";
import { useScrollBarDimensions } from "./useScrollBarDimensions";
import { stringMapDefault } from "./OverflowScroller.stringMap";
const componentName = "OverflowScroller";
export const OverflowScroller = forwardRef((props, ref) => {
    const { children, maxHeight, isLoading, maxWidth, style, onScroll, isSticky = defaults.isSticky, scrollRef, stringMap: stringMapProp, yAxis = { ...defaults.yAxis, ...props.yAxis }, xAxis = { ...defaults.xAxis, ...props.xAxis }, zIndexBase = 0, ...otherProps } = props;
    const stringMap = useStringMap(componentName, { stringMapDefault, stringMapProp });
    const outerRef = useRef(null);
    const overflowRef = useRef(null);
    const contentRef = useRef(null);
    const indicatorUpRef = useRef(null);
    const indicatorDownRef = useRef(null);
    const indicatorLeftRef = useRef(null);
    const indicatorRightRef = useRef(null);
    const { updateOverflowState, overflowState } = useOverflowState({
        overflowRef,
        contentRef,
        onScrollEndX: xAxis.onScrollEnd,
        onScrollEndY: yAxis.onScrollEnd
    });
    const { scrollBarHeight, scrollBarWidth } = useScrollBarDimensions(overflowRef);
    const scrollNext = useOverflowCallbacks(overflowRef);
    const arrowProps = {
        outerRef,
        isSticky,
        scrollBarWidth,
        scrollBarHeight,
        zIndexBase
    };
    return (_jsxs(Styled.OverflowScroller, { "data-testid": "OverflowScroller", ref: useCombineRefs(outerRef, ref), ...otherProps, style: { maxHeight, maxWidth, ...style }, children: [_jsx(OverflowIndicator, { direction: "left", buttonRef: indicatorLeftRef, focusFallbackRef: indicatorRightRef, label: stringMap.leftArrow, isVisible: overflowState.left && xAxis.hasIndicators !== false, align: xAxis.offset ? "flex-start" : "center", offsetY: xAxis.offset, onClick: scrollNext.left, ...arrowProps }), _jsx(OverflowIndicator, { direction: "up", focusFallbackRef: indicatorDownRef, buttonRef: indicatorUpRef, label: stringMap.topArrow, isVisible: overflowState.up && yAxis.hasIndicators !== false, onClick: scrollNext.up, ...arrowProps }), _jsx(OverflowIndicator, { direction: "right", buttonRef: indicatorRightRef, focusFallbackRef: indicatorLeftRef, label: stringMap.rightArrow, isVisible: overflowState.right && xAxis.hasIndicators !== false, offsetY: xAxis.offset, align: xAxis.offset ? "flex-start" : "center", onClick: scrollNext.right, ...arrowProps }), _jsx(OverflowIndicator, { direction: "down", buttonRef: indicatorDownRef, focusFallbackRef: indicatorUpRef, label: stringMap.bottomArrow, isVisible: overflowState.down && yAxis.hasIndicators !== false, onClick: scrollNext.down, ...arrowProps }), isLoading && _jsx(Styled.LoadingIndicator, { label: stringMap.loading }), _jsx(Styled.ScrollContainer, { ref: useCombineRefs(overflowRef, scrollRef), onScroll: (e) => {
                    updateOverflowState();
                    onScroll && onScroll(e);
                }, children: _jsx("div", { ref: contentRef, children: children }) })] }));
});
OverflowScroller.displayName = componentName;
